import React from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { Button } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import { EmptyState } from "@/components/ui/EmptyState";
import { Feather, MaterialCommunityIcons } from "@expo/vector-icons";
import { cn } from "@/lib/utils";
import type { TranslationKeys, SupportedLocale } from "@chinese-vendor/i18n";
import type { ActivityLogEntry, ActivityLogEventType } from "@/lib/api/review";
import { formatRelativeTime, formatSplitDateTime } from "@/lib/dateFormat";

interface ActivityLogTabProps {
  entries: ActivityLogEntry[] | undefined;
  t: ReturnType<typeof import("@/context/I18nContext").useI18n>["t"];
  locale: SupportedLocale;
}

const ACTIVITY_LOG_EVENT_CONFIG: Record<
  ActivityLogEventType,
  { iconComponent: React.ComponentType<any>; icon: string; bgClass: string }
> = {
  approved: { iconComponent: Feather, icon: "check", bgClass: "bg-green-50" },
  rejected: { iconComponent: Feather, icon: "x", bgClass: "bg-error" },
  submitted: { iconComponent: Feather, icon: "file-text", bgClass: "bg-blue-40" },
  resubmitted: { iconComponent: Feather, icon: "file-text", bgClass: "bg-blue-40" },
  requested_updates: {
    iconComponent: MaterialCommunityIcons,
    icon: "refresh",
    bgClass: "bg-orange",
  },
};

export function ActivityLogTab({ entries, t, locale }: ActivityLogTabProps) {
  if (!entries || entries.length === 0) {
    return (
      <View className="flex-1 items-center justify-center py-20">
        <EmptyState description="review.application.activityLog.empty" />
      </View>
    );
  }

  return (
    <View className="max-w-4xl py-6 items-start">
      <AppText weight="bold" className="text-2xl text-dark-text-50 mb-6">
        {t("review.application.activityLog.title")}
      </AppText>

      <View>
        {entries.map((entry, index) => {
          const config = ACTIVITY_LOG_EVENT_CONFIG[entry.type] ?? {
            iconComponent: Feather,
            icon: "info",
            bgClass: "bg-dark-text-20",
          };
          const isLast = index === entries.length - 1;
          const { date, time } = formatSplitDateTime(entry.timestamp, locale);
          const actionKey =
            `review.application.activityLog.actions.${entry.type.toLowerCase()}` as TranslationKeys;

          return (
            <View key={entry.id} className="flex-row gap-4">
              {/* Left: icon circle + connector line */}
              <View className="flex-col items-center">
                <View
                  className={cn(
                    "w-8 h-8 rounded-full items-center justify-center flex-shrink-0",
                    config.bgClass
                  )}
                >
                  <Icon
                    as={config.iconComponent}
                    name={config.icon}
                    size={16}
                    className="text-white"
                  />
                </View>
                {!isLast && <View className="w-px flex-1 bg-stroke-50 my-1" />}
              </View>

              {/* Right: content */}
              <View className={cn("flex-1 items-start pt-1", isLast ? "pb-6" : "pb-12")}>
                {/* Actor + action */}
                <View className="flex-row flex-wrap items-center gap-1 mb-2">
                  <AppText weight="semibold" className="text-lg text-dark-text-50">
                    {entry.actorName}
                  </AppText>
                  <AppText className="text-lg text-supporting-text">{t(actionKey)}</AppText>
                </View>

                {/* "View Request" button for requested_updates */}
                {entry.type === "requested_updates" && (
                  <View className="mb-2">
                    <Button
                      label="review.application.activityLog.viewRequest"
                      onPress={() => {}}
                      variant="secondary"
                      className="self-start"
                    />
                  </View>
                )}

                {/* Timestamps */}
                <View className="gap-0.5 items-start">
                  <AppText className="text-lg text-supporting-text">
                    {formatRelativeTime(entry.timestamp, locale, t)}
                  </AppText>
                  <View className="flex-row items-center gap-1">
                    <AppText className="text-sm text-dark-text-20">{date}</AppText>
                    <View className="w-1 h-1 rounded-full bg-dark-text-20" />
                    <AppText className="text-sm text-dark-text-20">{time}</AppText>
                  </View>
                </View>
              </View>
            </View>
          );
        })}
      </View>
    </View>
  );
}
